// src/features/goals/context/GoalsContext.jsx
import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { DateTime } from 'luxon';
import { useAuth } from '../../auth/context/AuthContext';
import * as goalsService from '../services/goalsService';
import { trackGoalProgressUpdated, trackGoalDeleted, trackGoalCompleted } from '@/shared/utils/analytics/analytics';

const GoalsContext = createContext();

const generateTempId = () => `temp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

export function GoalsProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [goals, setGoals] = useState([]);
  // Stable ref so callbacks always read the latest goals without being recreated on every change
  const goalsRef = useRef([]);
  goalsRef.current = goals;
  // Tracks in-flight logProgress calls to prevent double-completion race
  const processingGoals = useRef(new Set());

  // Subscribe to goals with real-time updates
  // Goals are normalized via normalizeGoal() in the service layer
  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setGoals([]);
      return;
    }

    const unsubscribe = goalsService.subscribeToGoals(user.uid, (goalsData) => {
      setGoals(goalsData);
    });

    return unsubscribe;
  }, [user?.uid, authLoading]);

  const addGoal = useCallback(async (goalData) => {
    if (!user) return;

    const tempId = generateTempId();
    const optimisticGoal = {
      id: tempId,
      _tempId: tempId,
      uid: user.uid,
      title: goalData.title,
      description: goalData.description || null,
      goalType: goalData.goalType || 'habit',
      targetType: goalData.targetType || 'frequency',
      targetValue: goalData.targetValue || 1,
      timeframe: goalData.timeframe || 'daily',
      trackingSource: goalData.trackingSource || 'manual',
      category: goalData.category || 'other',
      status: 'active',
      streak: 0,
      longestStreak: 0,
      lastCompleted: null,
      createdAt: new Date(),
    };
    setGoals(prev => [optimisticGoal, ...prev]);

    try {
      await goalsService.createGoal(user.uid, goalData);
      // onSnapshot replaces the optimistic entry with the real one
    } catch (error) {
      console.error('[GoalsContext] addGoal failed, rolling back:', error);
      setGoals(prev => prev.filter(g => g._tempId !== tempId));
    }
  }, [user]);

  const updateGoal = useCallback(async (goalId, updates) => {
    if (!user) return;

    // Capture original from ref (not goals state) so this callback is stable
    const original = goalsRef.current.find(g => g.id === goalId);
    setGoals(prev => prev.map(g => g.id === goalId ? { ...g, ...updates } : g));

    try {
      await goalsService.updateGoal(goalId, updates);
    } catch (error) {
      console.error('[GoalsContext] updateGoal failed, rolling back:', error);
      if (original) {
        setGoals(prev => prev.map(g => g.id === goalId ? original : g));
      }
    }
  }, [user]);

  const deleteGoal = useCallback(async (goalId) => {
    if (!user) return;

    const removed = goalsRef.current.find(g => g.id === goalId);
    const progress = removed ? goalsService.calculateGoalProgress(removed, []) : { percentage: 0 };

    // Optimistic: remove immediately
    setGoals(prev => prev.filter(g => g.id !== goalId));

    // If this is a temp (unsynced) goal, just remove from local state — no Firestore doc exists yet
    if (removed?._tempId) return;

    try {
      await goalsService.deleteGoal(goalId);
      trackGoalDeleted(goalId, progress.percentage);
    } catch (error) {
      console.error('[GoalsContext] deleteGoal failed, rolling back:', error);
      if (removed) setGoals(prev => [...prev, removed]);
    }
  }, [user]);

  const archiveGoal = useCallback(async (goalId) => {
    try {
      await goalsService.archiveGoal(goalId);
    } catch (error) {
      console.error('[GoalsContext] archiveGoal failed:', error);
      throw error;
    }
  }, []);

  const logProgress = useCallback(async (goalId, value = 1, date = new Date()) => {
    if (!user) return;

    // Prevent double-completion race: ignore if already processing this goal
    if (processingGoals.current.has(goalId)) return;
    processingGoals.current.add(goalId);

    const goal = goalsRef.current.find(g => g.id === goalId);
    if (!goal) { processingGoals.current.delete(goalId); return; }

    // Optimistic: compute streak using the same logic as the service
    const effectiveDate = goalsService.getEffectiveDate(date);
    const lastCompleted = goal.lastCompleted?.toDate
      ? DateTime.fromJSDate(goal.lastCompleted.toDate()).startOf('day')
      : null;
    let newStreak = goal.streak || 0;
    if (!lastCompleted) {
      newStreak = 1;
    } else {
      const daysDiff = Math.round(effectiveDate.diff(lastCompleted, 'days').days);
      if (daysDiff === 1) newStreak += 1;
      else if (daysDiff > 1) newStreak = 1;
      // daysDiff <= 0: same day — keep streak unchanged
    }
    const newLongestStreak = Math.max(goal.longestStreak || 0, newStreak);
    setGoals(prev => prev.map(g =>
      g.id === goalId
        ? { ...g, lastCompleted: date, streak: newStreak, longestStreak: newLongestStreak }
        : g
    ));

    try {
      await goalsService.logGoalProgress(goalId, goal, value, date);

      const progress = goalsService.calculateGoalProgress(goal, []);
      trackGoalProgressUpdated(progress.percentage);

      if (progress.percentage >= 100) {
        const startDate = goal.startDate?.toDate?.() || new Date(goal.startDate);
        const daysToComplete = Math.floor((Date.now() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        trackGoalCompleted(goalId, daysToComplete);
      }
    } catch (error) {
      console.error('[GoalsContext] logProgress failed, rolling back:', error);
      setGoals(prev => prev.map(g =>
        g.id === goalId
          ? { ...g, lastCompleted: goal.lastCompleted, streak: goal.streak, longestStreak: goal.longestStreak }
          : g
      ));
    } finally {
      processingGoals.current.delete(goalId);
    }
  }, [user]);

  const calculateGoalProgress = useCallback((goal, journalEntries = []) => {
    return goalsService.calculateGoalProgress(goal, journalEntries);
  }, []);

  const contextValue = useMemo(() => ({
    goals,
    addGoal,
    updateGoal,
    deleteGoal,
    archiveGoal,
    logProgress,
    calculateGoalProgress,
  }), [goals, addGoal, updateGoal, deleteGoal, archiveGoal, logProgress, calculateGoalProgress]);

  return (
    <GoalsContext.Provider value={contextValue}>
      {children}
    </GoalsContext.Provider>
  );
}

export const useGoals = () => useContext(GoalsContext);
